import { useCallback, useEffect, useMemo } from 'react';
import { validate as validateUuid } from 'uuid';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import CustomSelect from '@ui/select/custom-select/custom-select';
import OptionalComponent from '@ui/optional-component/optional-component';
import type { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { loadAudienceMembers, resetAudienceMembers } from '@/store/slices/audiences';
import {
  selectAudienceMembersSource,
  selectLoadingPasscodeLists,
  selectPasscodeLists,
} from '@/store/slices/audiences/selectors';
import type { AudienceMembersSource } from '@/store/slices/audiences/types';
import useLoadPasscodeLists from '@/micros/homepage/audiences/utils/use-load-passcode-lists';
import { UserStatus } from '@/types/all-attendees.types';
import type { IMultiSelectOption } from '@/types/email.types';
import AudienceMembersModal from './audience-members-modal';
import DynamicAudienceChip from './dynamic-audience-chip';
import classes from './audience-list-select.module.css';

export type AudienceSelection = { audiences: IMultiSelectOption[]; dynamic_audiences: UserStatus[] };

interface Props {
  audiences: IMultiSelectOption[] | undefined;
  dynamicAudiences: UserStatus[] | undefined;
  onChange: (selection: AudienceSelection) => void;
  disabled?: boolean;
}

type AudienceOptionMeta = { isDynamic: true; status: UserStatus } | { isDynamic: false; passcodeListId?: number };

const dynamicListOptions: TReactSelectOption<string, UserStatus, AudienceOptionMeta>[] = [
  { label: 'Registered', value: UserStatus.REGISTERED, meta: { isDynamic: true, status: UserStatus.REGISTERED } },
  { label: 'Attended', value: UserStatus.ATTENDED, meta: { isDynamic: true, status: UserStatus.ATTENDED } },
  { label: 'No Shows', value: UserStatus.NO_SHOWS, meta: { isDynamic: true, status: UserStatus.NO_SHOWS } },
];

const getAudienceMembersSource = (option: TReactSelectOption): AudienceMembersSource | null => {
  const meta = option.meta as AudienceOptionMeta | undefined;
  if (meta?.isDynamic) return { label: option.label, status: meta.status };
  if (meta?.passcodeListId) return { label: option.label, passcodeListId: meta.passcodeListId };
  return null;
};

const getSelectedDynamicListOptions = (selectedDynamicAudience: UserStatus[] = []) => {
  return dynamicListOptions.filter((option) => selectedDynamicAudience.includes(option.value));
};

function AudienceListSelect({ audiences, dynamicAudiences, onChange, disabled }: Props) {
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const loadingPasscodeLists = useAppSelector(selectLoadingPasscodeLists);
  const viewingAudience = useAppSelector(selectAudienceMembersSource);
  const dispatch = useAppDispatch();
  useLoadPasscodeLists();

  // the members modal lives in the store, so close it if the select goes away while it's open
  useEffect(() => {
    return () => {
      dispatch(resetAudienceMembers());
    };
  }, [dispatch]);

  const audienceOptions = useMemo(() => {
    const audienceListOptions = (passcodeLists ?? []).map(
      (audience): TReactSelectOption<string, string, AudienceOptionMeta> => ({
        label: audience.name,
        value: audience.uuid,
        meta: { isDynamic: false, passcodeListId: audience.passcode_list },
      })
    );
    return [...dynamicListOptions, ...audienceListOptions];
  }, [passcodeLists]);

  const selectedAudiences = useMemo(() => {
    if (audienceOptions.length && !disabled) {
      return [...(audiences ?? []), ...getSelectedDynamicListOptions(dynamicAudiences ?? [])];
    }

    return [];
  }, [disabled, audienceOptions, audiences, dynamicAudiences]);

  const onChangeAudiences = useCallback(
    (options: TReactSelectOption[]) => {
      const audiences: IMultiSelectOption[] = [];
      const selectedDynamicAudiences: UserStatus[] = [];

      options.forEach((option) => {
        if (validateUuid(option.value)) {
          audiences.push({ label: option.label, value: String(option.value) });
        } else if (
          [UserStatus.REGISTERED, UserStatus.ATTENDED, UserStatus.NO_SHOWS].includes(option.value as UserStatus)
        ) {
          selectedDynamicAudiences.push(option.value as UserStatus);
        }
      });

      // only one dynamic list can be selected at a time - a newly picked one replaces the previous selection
      const previousDynamicAudiences = dynamicAudiences ?? [];
      const dynamicAudience =
        selectedDynamicAudiences.find((status) => !previousDynamicAudiences.includes(status)) ??
        selectedDynamicAudiences.at(-1);
      onChange({ audiences, dynamic_audiences: dynamicAudience ? [dynamicAudience] : [] });
    },
    [onChange, dynamicAudiences]
  );

  const renderAudienceOption = useCallback(
    ({ option }: { option: TReactSelectOption }) => {
      const membersSource = getAudienceMembersSource(option);
      return (
        <div className={classes.audienceOption}>
          <Text clamp={1} wordBreak="break-all">
            {option.label}
          </Text>
          <OptionalComponent display={!!(option?.meta as AudienceOptionMeta | undefined)?.isDynamic}>
            <DynamicAudienceChip />
          </OptionalComponent>
          {membersSource ? (
            <Button
              clear
              className={classes.viewMembersButton}
              aria-label={`View ${option.label} members`}
              onClick={(event) => {
                // keep the click from also toggling the option's selection
                event.stopPropagation();
                dispatch(loadAudienceMembers({ source: membersSource }));
              }}
            >
              <Icon name={ICONS.SHOW_EYE} size={14} color="var(--global-ico-text-2)" />
            </Button>
          ) : null}
        </div>
      );
    },
    [dispatch]
  );

  return (
    <div className={classes.audienceSelect}>
      <CustomSelect
        id="schedule-audience-input"
        Placeholder="Select audiences"
        options={audienceOptions}
        controlledOptions={selectedAudiences}
        onChange={onChangeAudiences}
        disabled={loadingPasscodeLists || disabled}
        filterSelected
        inputPadding="0px 14px"
        clearInputOnMouseSelect
        isMultiSelect
        direction="vertical"
        maxRows={12}
        hideHeader
        hideMenu={!!viewingAudience}
        RenderMenuOptionContent={renderAudienceOption}
      />
      <AudienceMembersModal />
    </div>
  );
}

export default AudienceListSelect;
